import { useRef, useState } from 'react';

import { PaintBrushIcon } from '@heroicons/react/24/solid';
import { useClickOutside } from 'apps/web/hooks/useClickOutside';
import clsx from 'clsx';

import { Button } from '../Button';

import { CollapsibleThemePanel } from './CollapsibleThemePanel';
import { formClasses } from './utils/FormStyles';

export interface ThemeOption {
  id: string;
  name: string;
  backgroundImageTitle?: string;
  background?: string;
  text?: string;
  backgroundImageContent?: string;
  [key: string]: unknown;
}

interface Style {
  container?: string;
  label?: string;
  button?: string;
}

interface ThemeSelectorFieldProps {
  label?: React.ReactNode;
  value: ThemeOption;
  onChange: (value: ThemeOption) => void;
  options: ThemeOption[];
  errorMessage?: string;
  id?: string;
  className?: Style;
  disabled?: boolean;
  tooltip?: string;
  link?: string;
  slideTitle: string;
  highlight?: boolean;
}

export function ThemeSelectorField({
  label,
  value,
  onChange,
  options,
  errorMessage,
  id = 'theme-selector',
  className,
  disabled = false,
  tooltip,
  slideTitle,
  highlight = false,
}: ThemeSelectorFieldProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useClickOutside(selectorRef, isPanelOpen, () => setIsPanelOpen(false));

  // Function to get background color or image style
  const getThemeBackgroundStyle = (theme: ThemeOption) => {
    if (theme.backgroundImageTitle) {
      return {
        backgroundImage: `url(${theme.backgroundImageTitle})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }

    // For themes without background images, use background color
    return {
      backgroundColor: theme.background || '#FFFFFF',
    };
  };

  const handleThemeChange = (newTheme: ThemeOption) => {
    onChange(newTheme);
    setIsPanelOpen(false);
  };

  return (
    <div
      className={clsx('flex flex-row items-center gap-2 w-full', className.container)}
    >
      {label && (
        <label className={clsx('flex h-full', className.label)} htmlFor={id}>
          {label}:
        </label>
      )}
      <Button
        disabled={disabled}
        id={id}
        className={clsx(
          'h-[38px] w-full bg-white p-0',
          highlight && 'animate-ring-pulse ring-2 ring-blue-500 ring-offset-2',
          className.button,
          formClasses,
        )}
        onClick={() => !disabled && setIsPanelOpen(!isPanelOpen)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div ref={selectorRef} className="relative h-full">
          <div
            className={clsx(
              'relative inline-flex h-full w-full items-center gap-2 text-sm font-medium transition-colors',
              disabled
                ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                : 'border-gray-200 text-gray-700 hover:bg-gray-50',
            )}
          >
            <div className="relative aspect-video h-5 overflow-hidden rounded">
              <div
                className="absolute inset-0 h-full w-full rounded border border-gray-300"
                style={getThemeBackgroundStyle(value)}
              />
            </div>
            <span className="flex-1 text-left">{value.name}</span>
            <PaintBrushIcon
              className={clsx(
                'h-4 w-4',
                disabled ? 'text-gray-400' : 'text-gray-500',
              )}
            />
          </div>
          {highlight && tooltip && !showTooltip && !isPanelOpen && (
            <div className="absolute left-1/2 -top-20 z-50 hidden w-full -translate-x-1/2 animate-bounce sm:left-3/4 sm:block sm:translate-x-0">
              <div className="bg-turquoise relative rounded-md border border-gray-200 p-2 text-sm text-white shadow-md">
                {tooltip}
                <div className="bg-turquoise absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 transform border-b border-r border-gray-200 shadow-lg sm:left-4 sm:translate-x-0" />
              </div>
            </div>
          )}

          {tooltip && showTooltip && !isPanelOpen && (
            <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-600 shadow-md">
              {tooltip}
            </div>
          )}

          {isPanelOpen && (
            <div
              ref={panelRef}
              className="fixed inset-x-0 bottom-0 z-50 w-full overflow-hidden rounded-t-lg border-t border-gray-200 bg-white shadow-xl sm:absolute sm:inset-x-auto sm:bottom-auto sm:top-full sm:-left-48 sm:mt-1 sm:w-[calc(100vw-2rem)] sm:max-w-[600px] sm:rounded-lg sm:border xl:left-0"
            >
              <div className="max-h-[70vh] overflow-y-auto p-1">
                <CollapsibleThemePanel
                  className="w-full"
                  options={options}
                  slideTitle={slideTitle}
                  value={value}
                  onChange={handleThemeChange}
                />
              </div>
              <div className="flex items-center justify-end border-t border-gray-200 bg-gray-50 p-2">
                <Button
                  className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsPanelOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>

        {errorMessage && (
          <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
        )}
      </Button>
    </div>
  );
}
